import { useState } from 'react';
import { createPortal } from 'react-dom';
import { XIcon, BellIcon } from './Icons';

// Small "How this works" helper. Pass a title and an array of steps/sections.
export function HowItWorksButton({ title, sections }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold border border-slate-200 text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] transition-all">
        <BellIcon size={13} /> How this works
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: 'rgba(15,27,51,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={e => e.target === e.currentTarget && setOpen(false)}>
          <div className="bg-white rounded-2xl w-[620px] max-w-full max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100 flex-shrink-0">
              <div className="w-10 h-10 rounded-xl bg-[#0f1b33] flex items-center justify-center">
                <BellIcon size={18} className="text-white" />
              </div>
              <h2 className="flex-1 text-base font-bold text-slate-800">{title}</h2>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all">
                <XIcon size={16} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 space-y-5">
              {sections.map((s, i) => (
                <div key={i}>
                  <h3 className="text-[13.5px] font-bold text-[#e8614a] mb-1.5">{s.heading}</h3>
                  {s.lines.map((l, j) => (
                    <p key={j} className="text-[13.5px] text-slate-600 leading-relaxed mb-1.5">{l}</p>
                  ))}
                </div>
              ))}
            </div>
            <div className="px-6 pb-5 flex justify-end flex-shrink-0">
              <button onClick={() => setOpen(false)}
                className="px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all">
                Got it
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export const TIMESHEET_HELP = [
  { heading: '1. What this page is for', lines: [
    'This is the monthly attendance register. Every worker starts with P (Present) on every day — you only click the days that were different.',
  ]},
  { heading: '2. Marking a day', lines: [
    'Click any box to change it. Each click moves to the next code: P → A (Absent) → O (Day Off) → SL (Sick) → UL (Unpaid Leave) → PH (Public Holiday) → NE (Not Employed) → LWD (Last Working Day) → back to P.',
    'A and UL and NE reduce salary. SL (sick with Sehaty), O, PH and LWD are paid — no money is cut.',
  ]},
  { heading: '3. Overtime (OT box)', lines: [
    'Type the overtime money amount in Riyals that the client calculated for this worker this month — for example 150. This exact amount is added on top of the salary. No hours, no formulas.',
  ]},
  { heading: '4. Worker moves to another client mid-month — 3 steps, ALWAYS all 3', lines: [
    'STEP 1: In the OLD client\u2019s sheet, mark his last day there as LWD (purple, paid).',
    'STEP 2: Edit the employee and change his Project to the new client. (Do LWD FIRST, project change SECOND — after the change he leaves the old roster.)',
    'STEP 3: Open the NEW client\u2019s sheet and mark his days BEFORE the transfer as NE. If you skip this, those days count as Present in BOTH sheets = DOUBLE PAY.',
    'Payroll automatically combines both sheets into one payslip. He stays visible in the old sheet with a purple "moved to..." note.',
  ]},
  { heading: '5. If salary also changes with the transfer', lines: [
    'RULE: salary changes always take effect from the 1st of the NEXT month. Finalize the current month on the OLD salary first, then change the salary.',
    'If the worker must get the higher rate for his days at the new client this month: pay the difference through the OT box (days at new client \u00d7 difference \u00f7 30) and write a note. Never change salary mid-month and then finalize.',
  ]},
  { heading: '6. Save and Lock', lines: [
    'Save keeps your marks. Lock Month freezes the whole sheet when the month is checked and complete — nobody can change it after (Unlock is admin-only).',
    'The order every month: mark attendance and OT → Save → Lock → then go to Payroll and Finalize.',
  ]},
];

export const PAYROLL_HELP = [
  { heading: '1. What this page shows', lines: [
    'One row per worker: salary, unpaid days from the timesheet, money cut for absence, loan/advance installment, overtime added, and the final Net Pay.',
    'Until you press Finalize, everything here is a LIVE PREVIEW — it recalculates every time the timesheet changes. Nothing is deducted yet.',
  ]},
  { heading: '2. How the numbers are calculated', lines: [
    'One unpaid day costs salary \u00f7 30. Example: salary 1,500 \u2192 one absent day cuts 50.',
    'Absent (A), Unpaid Leave (UL) and Not Employed (NE) days are unpaid. Sick (SL), Day Off (O) and Public Holiday (PH) are paid in full.',
    'Overtime is the exact Riyal amount typed in the timesheet OT box — added on top.',
  ]},
  { heading: '3. Deductions (loans / advances)', lines: [
    'If a worker owes money (set in his profile as Outstanding Deduction), payroll takes maximum 500 per month until the balance reaches zero.',
    'The cut happens ONLY when you press Finalize — and it is written into the worker\u2019s History automatically.',
  ]},
  { heading: '4. Finalize Month — the important button', lines: [
    'Finalize freezes the month: numbers are locked as a permanent record, deduction installments are actually taken, and payslips/PDF/Excel become available.',
    'Press it only AFTER the timesheet is complete and locked. If you change a timesheet after finalizing, a yellow warning appears — press Undo, then Finalize again to refresh.',
  ]},
  { heading: '5. When a worker leaves (termination)', lines: [
    'NEVER just set status to Terminated — the system blocks it. Open the worker\u2019s profile and use the amber "Terminate & Settle" button.',
    'It asks for the Last Working Day, then automatically calculates the full final settlement: worked days that month + overtime + End-of-Service Benefit (Saudi labor law) + leave encashment \u2212 ALL remaining deductions (no 500 cap at settlement).',
    'Confirming freezes the settlement permanently, downloads the signed-ready statement PDF, and removes him from future timesheets and payroll.',
    'His old attendance is never deleted: open his profile anytime (even after termination) and use Timesheet PDF / Timesheet Excel to export his full day-by-day record.',
  ]},
  { heading: '6. Monthly routine (do it in this order)', lines: [
    '1) Timesheets: mark attendance + OT \u2192 Save \u2192 Lock Month.',
    '2) Payroll: check the numbers \u2192 Finalize Month.',
    '3) Download Excel/PDF, print payslips, pay salaries.',
    '4) Never edit that month again. Salary changes take effect from the NEXT month.',
  ]},
];